import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../config/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import {
  Box,
  Card,
  CardMedia,
  Typography,
  CircularProgress,
  Button,
  Grid,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { applySeo, DEFAULT_TITLE } from "../utils/seo";

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // ✅ Try to load from cache first
        const cachedEvent = localStorage.getItem(`event_${id}`);
        if (cachedEvent) {
          setEvent(JSON.parse(cachedEvent));
          setLoading(false);
          return;
        }

        // ✅ Fetch from Firestore for specific doc id
        const eventRef = doc(db, "events", id);
        const eventSnap = await getDoc(eventRef);

        if (eventSnap.exists()) {
          const eventData = { id: eventSnap.id, ...eventSnap.data() };

          // Cache for future visits
          localStorage.setItem(`event_${id}`, JSON.stringify(eventData));

          setEvent(eventData);
        } else {
          setErrorMsg("Event not found or may have been deleted.");
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        setErrorMsg("Error loading event details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  useEffect(() => {
    if (!event) return;
    const description = String(event.description || "")
      .replace(/\s+/g, " ")
      .trim();
    const shortDescription = description
      ? description.slice(0, 160)
      : "View the latest event details from Rising Stars Public School.";
    const imageUrl = Array.isArray(event.imageUrls)
      ? event.imageUrls.find((url) => !String(url).toLowerCase().includes(".mp4")) ||
        event.imageUrls[0]
      : "";
    const url = `${window.location.origin}/events/${event.id}`;
    applySeo({
      title: `${event.eventName} | ${DEFAULT_TITLE}`,
      description: shortDescription,
      image: imageUrl,
      url,
      type: "article",
    });
  }, [event]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box textAlign="center" mt={8}>
        <Typography variant="h6" color="error">
          {errorMsg}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/")}
          sx={{ mt: 2 }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  if (!event) return null;

  return (
    <Box p={{ xs: 2, sm: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/")}
        sx={{ mb: 3 }}
      >
        Back to Events
      </Button>

      <Card
        sx={{
          borderRadius: 4,
          boxShadow: 4,
          p: 3,
          maxWidth: 900,
          mx: "auto",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="center"
          mb={2}
          color="primary"
        >
          {event.eventName}
        </Typography>

        <Typography
          variant="subtitle1"
          textAlign="center"
          color="text.secondary"
          mb={3}
        >
          {event.date}
        </Typography>

        {event.description && (
          <Typography
            variant="body1"
            color="text.secondary"
            mb={4}
            textAlign="center"
          >
            {event.description}
          </Typography>
        )}

        <Grid container spacing={2}>
          {event.imageUrls && event.imageUrls.length > 0 ? (
            event.imageUrls.map((url, i) => {
              const isVideo = url.toLowerCase().includes("mp4");
              return (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  {isVideo ? (
                    <CardMedia
                      component="video"
                      controls
                      src={url}
                      sx={{
                        borderRadius: 2,
                        height: 180,
                        boxShadow: 2,
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <CardMedia
                      component="img"
                      image={url}
                      alt={`event-media-${i}`}
                      sx={{
                        borderRadius: 2,
                        height: 180,
                        boxShadow: 2,
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Grid>
              );
            })
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              mt={2}
              textAlign="center"
              width="100%"
            >
              No media uploaded for this event.
            </Typography>
          )}
        </Grid>
      </Card>
    </Box>
  );
}

export default EventDetail;
